/**
 * Redimensionamento de imagens no navegador, usado antes de enviar anexos do
 * paciente. Reencodar em canvas também descarta EXIF/GPS da foto original.
 */

const DEFAULT_MAX_SIZE = 1600;
const DEFAULT_QUALITY = 0.85;
const THUMB_MAX_SIZE = 320;
const THUMB_QUALITY = 0.75;

/**
 * Calcula o tamanho final mantendo a proporção. Não amplia imagens pequenas.
 */
const getTargetSize = (width, height, maxSize) => {
    const largest = Math.max(width, height);

    if (!largest || largest <= maxSize) {
        return { width: width, height: height };
    }

    const ratio = maxSize / largest;

    return {
        width: Math.max(1, Math.round(width * ratio)),
        height: Math.max(1, Math.round(height * ratio))
    };
};

/**
 * Decodifica o arquivo respeitando a orientação EXIF quando o navegador suporta
 * createImageBitmap; senão cai para <img>, sem correção de orientação.
 */
const decodeImage = async (file) => {
    if (typeof createImageBitmap === 'function') {
        try {
            const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
            return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close && bitmap.close() };
        } catch (e) {
            // navegador antigo ou sem suporte à opção: usa o fallback abaixo
        }
    }

    return await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            resolve({
                source: image,
                width: image.naturalWidth,
                height: image.naturalHeight,
                release: () => URL.revokeObjectURL(url)
            });
        };

        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Não foi possível ler a imagem.'));
        };

        image.src = url;
    });
};

const canvasToBlob = (canvas, quality) => {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Não foi possível processar a imagem.'));
            }
        }, 'image/jpeg', quality);
    });
};

/**
 * Redimensiona um arquivo de imagem para JPEG.
 * @param {File|Blob} file arquivo escolhido pelo usuário
 * @param {{maxSize?: number, quality?: number}} options
 * @returns {Promise<{blob: Blob, width: number, height: number}>}
 */
export const resizeImageFile = async (file, options = {}) => {
    const maxSize = options.maxSize || DEFAULT_MAX_SIZE;
    const quality = options.quality || DEFAULT_QUALITY;

    const decoded = await decodeImage(file);

    try {
        const target = getTargetSize(decoded.width, decoded.height, maxSize);

        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;

        const context = canvas.getContext('2d');
        context.drawImage(decoded.source, 0, 0, target.width, target.height);

        const blob = await canvasToBlob(canvas, quality);

        return { blob: blob, width: target.width, height: target.height };
    } finally {
        if (decoded.release) decoded.release();
    }
};

/**
 * Prepara a imagem para upload: versão cheia (1600 px) e miniatura (320 px).
 * @param {File} file
 * @returns {Promise<{full: Blob, thumb: Blob, width: number, height: number}>}
 */
export const prepareImageForUpload = async (file) => {
    const full = await resizeImageFile(file, { maxSize: DEFAULT_MAX_SIZE, quality: DEFAULT_QUALITY });
    const thumb = await resizeImageFile(full.blob, { maxSize: THUMB_MAX_SIZE, quality: THUMB_QUALITY });

    return {
        full: full.blob,
        thumb: thumb.blob,
        width: full.width,
        height: full.height
    };
};
